import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { GpsWritePolicy } from "@/lib/gpsWritePolicy";
import { createDriverLocationWriter, type DriverLocationWriter } from "@/lib/driverLocationWriter";

interface LocationState {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: string;
}

interface UseWebDriverLocationOptions {
  updateInterval?: number; // milliseconds between location updates
  enableHighAccuracy?: boolean;
  /**
   * When true (default), the hook auto starts/stops tracking based on the `enabled` param.
   * Set to false when you want to control start/stop manually via the returned functions.
   */
  autoManage?: boolean;
}

export const useWebDriverLocation = (
  enabled: boolean = false,
  options: UseWebDriverLocationOptions = {}
) => {
  const [location, setLocation] = useState<LocationState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | null>(null);
  const { toast } = useToast();
  
  const watchIdRef = useRef<number | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const {
    // updateInterval kept for API compat but no longer used (duplicate getCurrentPosition removed for battery)
    updateInterval: _updateInterval = 15000,
    enableHighAccuracy = false, // Default LOW power; switch to high only when actively driving
    autoManage = true,
  } = options;

  // Same write policy as native (src/lib/gpsWritePolicy.ts): first fix, >300m
  // jump, otherwise at most one write per 60s — the browser can deliver a fix
  // every second, which previously wrote a history row every second.
  const writePolicyRef = useRef(new GpsWritePolicy());
  const writerRef = useRef<DriverLocationWriter | null>(null);
  if (!writerRef.current) writerRef.current = createDriverLocationWriter("useWebDriverLocation");
  const trackingActiveRef = useRef(false);

  const checkProximity = useCallback(async (lat: number, lng: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data: driver } = await supabase
        .from("drivers")
        .select("id")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (!driver) return;

      // Check for active delivery jobs with tracking shared and trigger proximity alert
      const { data: activeJobs } = await supabase
        .from("jobs")
        .select("id")
        .eq("driver_id", driver.id)
        .not("collection_completed_at", "is", null)
        .is("delivery_completed_at", null)
        .eq("tracking_shared", true)
        .eq("proximity_alert_sent", false)
        .neq("status", "cancelled")
        .neq("status", "archived")
        .is("cancelled_at", null);

      for (const job of activeJobs || []) {
        try {
          await supabase.functions.invoke("check-driver-proximity", {
            body: { jobId: job.id, driverLat: lat, driverLng: lng },
          });
        } catch (error) {
          console.error(`Error checking proximity for job ${job.id}:`, error);
        }
      }
    } catch (error) {
      console.error("[useWebDriverLocation] proximity check error:", error);
    }
  }, []);

  const commitWrite = useCallback(async (lat: number, lng: number) => {
    writePolicyRef.current.record({ lat, lng }, Date.now());
    const ok = await writerRef.current!.write(lat, lng);
    if (ok) await checkProximity(lat, lng);
  }, [checkProximity]);

  const handlePositionUpdate = useCallback((position: GeolocationPosition) => {
    const newLocation: LocationState = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: new Date().toISOString()
    };
    
    setLocation(newLocation);
    const now = Date.now();
    const reason = writePolicyRef.current.evaluate(
      { lat: newLocation.latitude, lng: newLocation.longitude },
      now,
    );
    if (reason) void commitWrite(newLocation.latitude, newLocation.longitude);
  }, [commitWrite]);

  const handlePositionError = useCallback((error: GeolocationPositionError) => {
    console.error('Geolocation error:', error);
    
    let errorMessage = 'Location error';
    switch (error.code) {
      case 1:
        errorMessage = 'Location permission denied';
        setPermissionStatus('denied');
        break;
      case 2:
        errorMessage = 'Location unavailable';
        break;
      case 3:
        errorMessage = 'Location request timed out';
        break;
    }
    
    setError(errorMessage);
  }, []);

  const startTracking = useCallback(async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      const msg = 'Geolocation is not supported by this browser';
      setError(msg);
      toast({
        title: "Location Not Supported",
        description: msg,
        variant: "destructive",
      });
      return false;
    }

    try {
      // Check permission status
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(result.state);
        
        if (result.state === 'denied') {
          const msg = 'Location permission denied. Please enable in browser settings.';
          setError(msg);
          toast({
            title: "Location Access Required",
            description: msg,
            variant: "destructive",
          });
          return false;
        }
      }

      // Get initial position
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handlePositionUpdate(position);
          setIsTracking(true);
          setError(null);
          setPermissionStatus('granted');
          console.log('[useWebDriverLocation] Location permission granted, tracking started');
        },
        (error) => {
          handlePositionError(error);
          // Only show toast for non-permission errors (permission denied is handled above)
          if (error.code !== 1) {
            toast({
              title: "Location Error",
              description: error.code === 2 ? "Location unavailable" : error.code === 3 ? "Location request timed out" : "Could not get location",
              variant: "destructive",
            });
          }
        },
        {
          enableHighAccuracy,
          timeout: 10000,
          maximumAge: 0
        }
      );

      // Set up continuous watching — single source of truth (no duplicate interval polling)
      watchIdRef.current = navigator.geolocation.watchPosition(
        handlePositionUpdate,
        handlePositionError,
        {
          enableHighAccuracy,
          timeout: 30000,
          maximumAge: 60000, // accept positions up to 60s old to reduce GPS wake-ups
        }
      );

      // No heartbeat/polling: browser watch readings are the only source,
      // throttled by GpsWritePolicy.
      trackingActiveRef.current = true;

      return true;
    } catch (error: any) {
      console.error("Error starting location tracking:", error);
      setError(error.message);
      toast({
        title: "Location Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  }, [enableHighAccuracy, handlePositionUpdate, handlePositionError, toast]);

  const stopTracking = useCallback(async (silent: boolean = false) => {
    // Clear watch
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    // Clear interval
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    setIsTracking(false);
    trackingActiveRef.current = false;
    writePolicyRef.current.reset();
    writerRef.current?.clear();

    // Mark driver as inactive
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: driver } = await supabase
        .from("drivers")
        .select("id")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (driver) {
        await supabase
          .from("drivers")
          .update({ is_active: false })
          .eq("id", driver.id);
      }
    } catch (error: any) {
      console.error("Error stopping tracking:", error);
    }

    // Only show toast if not silent and tracking was actually running
    if (!silent) {
      toast({
        title: "Location Tracking Stopped",
        description: "Your location is no longer being shared",
      });
    }
  }, [toast]);

  // Auto-start/stop based on enabled prop (opt-out via autoManage)
  useEffect(() => {
    if (!autoManage) return;

    if (enabled && !isTracking) {
      startTracking();
    } else if (!enabled && isTracking) {
      stopTracking();
    }
  }, [autoManage, enabled, isTracking, startTracking, stopTracking]);

  // Always cleanup watchers/intervals on unmount to avoid leaks (no toasts here)
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  return {
    location,
    error,
    isTracking,
    permissionStatus,
    startTracking,
    stopTracking,
  };
};
